// src/pages/InterviewAssistant/ProvocationInterview.tsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, BarChart2, Award
} from 'lucide-react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip
} from 'recharts';

// ------------------- данные шкал и провокационных вопросов -------------------
const scales = [
  {
    title: 'Стрессоустойчивость',
    description: 'Способность сохранять спокойствие и продуктивность в напряжённых ситуациях',
    questions: [
      {
        id: 1,
        text: 'Вы провалили важный проект, и руководство публично критикует вас на совещании. Ваша реакция?',
        options: [
          { value: 1, label: 'Молча выслушаю и уйду' },
          { value: 2, label: 'Начну оправдываться' },
          { value: 3, label: 'Признаю ошибки и предложу план исправления' },
          { value: 4, label: 'Спокойно отвечу на критику, приведу контраргументы' },
          { value: 5, label: 'Использую ситуацию для демонстрации своей устойчивости и готовности к росту' }
        ]
      },
      {
        id: 2,
        text: 'Вас неожиданно просят выступить с презентацией перед большой аудиторией через 10 минут. Ваши действия?',
        options: [
          { value: 1, label: 'Откажусь, сославшись на неготовность' },
          { value: 2, label: 'Соглашусь, но буду сильно нервничать' },
          { value: 3, label: 'Быстро соберу мысли и выступлю, хотя будет некомфортно' },
          { value: 4, label: 'Уверенно выйду и импровизирую на основе знаний' },
          { value: 5, label: 'Восприму это как вызов и возможность проявить себя' }
        ]
      }
    ]
  },
  {
    title: 'Управление эмоциями',
    description: 'Способность контролировать эмоции и не поддаваться на провокации',
    questions: [
      {
        id: 3,
        text: 'Коллега в агрессивной форме обвиняет вас в ошибке, которой вы не совершали. Что вы сделаете?',
        options: [
          { value: 1, label: 'Отвечу агрессией' },
          { value: 2, label: 'Проигнорирую, чтобы не конфликтовать' },
          { value: 3, label: 'Спокойно попрошу объяснить суть претензии' },
          { value: 4, label: 'Предложу конструктивно разобрать ситуацию после того, как эмоции утихнут' },
          { value: 5, label: 'Сохраняя хладнокровие, переведу диалог в русло фактов и совместного поиска решения' }
        ]
      },
      {
        id: 4,
        text: 'На переговорах партнёр делает провокационное замечание о вашей компетенции. Ваш ответ?',
        options: [
          { value: 1, label: 'Резко отвечу, защищая свою репутацию' },
          { value: 2, label: 'Сделаю вид, что не заметил' },
          { value: 3, label: 'Спрошу, на чём основано такое мнение' },
          { value: 4, label: 'Приведу конкретные примеры своих достижений' },
          { value: 5, label: 'Побла годарю за обратную связь и переведу разговор в деловое русло, подчеркнув общие цели' }
        ]
      }
    ]
  },
  {
    title: 'Аргументация',
    description: 'Умение отстаивать свою позицию и доносить её до оппонента',
    questions: [
      {
        id: 5,
        text: 'Ваше предложение отвергли без объяснения причин. Как вы поступите?',
        options: [
          { value: 1, label: 'Смирюсь и больше не буду предлагать' },
          { value: 2, label: 'Обижусь, но продолжу работать' },
          { value: 3, label: 'Попрошу конструктивную обратную связь' },
          { value: 4, label: 'Подготовлю более развёрнутое обоснование и представлю повторно' },
          { value: 5, label: 'Пойму причину отказа и адаптирую предложение так, чтобы оно отвечало интересам всех сторон' }
        ]
      },
      {
        id: 6,
        text: 'В споре оппонент использует эмоциональные аргументы. Ваша тактика?',
        options: [
          { value: 1, label: 'Поддамся эмоциям' },
          { value: 2, label: 'Прекращу дискуссию' },
          { value: 3, label: 'Попробую вернуть разговор в рациональное русло' },
          { value: 4, label: 'Признаю его чувства, но продолжу оперировать фактами' },
          { value: 5, label: 'Использую эмоциональный интеллект, чтобы сначала разрядить обстановку, затем перейти к логике' }
        ]
      }
    ]
  },
  {
    title: 'Уверенность',
    description: 'Степень уверенности в себе при столкновении с сомнениями или давлением',
    questions: [
      {
        id: 7,
        text: 'Вам говорят: "Вы уверены, что справитесь? Это слишком сложно для вас". Ваша реакция?',
        options: [
          { value: 1, label: 'Соглашусь и откажусь от задачи' },
          { value: 2, label: 'Попытаюсь, но сомневаясь в себе' },
          { value: 3, label: 'Скажу, что справлюсь, хотя внутри волнуюсь' },
          { value: 4, label: 'Приведу аргументы, почему я способен выполнить задачу' },
          { value: 5, label: 'Уверенно заявлю о своей компетенции и предложу обсудить план действий' }
        ]
      },
      {
        id: 8,
        text: 'Во время собеседования интервьюер намеренно занижает ваши достижения. Что вы чувствуете и делаете?',
        options: [
          { value: 1, label: 'Соглашаюсь, чтобы не спорить' },
          { value: 2, label: 'Начинаю сомневаться в себе' },
          { value: 3, label: 'Вежливо поправляю, указывая на фактические результаты' },
          { value: 4, label: 'Подкрепляю свои слова конкретными цифрами и примерами' },
          { value: 5, label: 'Сохраняя достоинство, превращаю критику в возможность рассказать о своих сильных сторонах' }
        ]
      }
    ]
  },
  {
    title: 'Гибкость мышления',
    description: 'Способность быстро переключаться и находить нестандартные решения',
    questions: [
      {
        id: 9,
        text: 'Вам дают задачу, которая противоречит вашему предыдущему опыту. Как вы отреагируете?',
        options: [
          { value: 1, label: 'Откажусь, так как это не моя специализация' },
          { value: 2, label: 'Попробую, но буду действовать по шаблону' },
          { value: 3, label: 'Изучу новые подходы и применю их' },
          { value: 4, label: 'Быстро перестроюсь и найду нестандартное решение' },
          { value: 5, label: 'Восприму это как возможность расширить кругозор и предложу инновационный подход' }
        ]
      },
      {
        id: 10,
        text: 'Вас просят сделать работу способом, который вы считаете неэффективным. Ваши действия?',
        options: [
          { value: 1, label: 'Сделаю, как просят, без возражений' },
          { value: 2, label: 'Выскажу недовольство, но подчинюсь' },
          { value: 3, label: 'Предложу альтернативный метод и обосную его' },
          { value: 4, label: 'Покажу на примере, почему мой способ лучше' },
          { value: 5, label: 'Найду компромисс, объединив лучшие элементы обоих подходов' }
        ]
      }
    ]
  },
  {
    title: 'Честность / искренность',
    description: 'Готовность говорить правду, даже если она неудобна',
    questions: [
      {
        id: 11,
        text: 'Руководитель просит вас приукрасить результаты в отчёте. Ваша реакция?',
        options: [
          { value: 1, label: 'Соглашусь, чтобы не портить отношения' },
          { value: 2, label: 'Сделаю минимальные изменения' },
          { value: 3, label: 'Объясню, что это некорректно, но сделаю' },
          { value: 4, label: 'Откажусь и предложу честный, но позитивный вариант' },
          { value: 5, label: 'Твёрдо откажусь, рискуя вызвать недовольство, но останусь верен принципам' }
        ]
      },
      {
        id: 12,
        text: 'На интервью вас спрашивают о причине ухода с предыдущего места. Правда может выставить вас не в лучшем свете. Что вы ответите?',
        options: [
          { value: 1, label: 'Совру, чтобы выглядеть лучше' },
          { value: 2, label: 'Расскажу полуправду' },
          { value: 3, label: 'Скажу правду, но очень дипломатично' },
          { value: 4, label: 'Честно расскажу, акцентируя извлечённые уроки' },
          { value: 5, label: 'Откровенно поделюсь опытом, показав зрелость и способность к рефлексии' }
        ]
      }
    ]
  },
  {
    title: 'Работа с критикой',
    description: 'Восприятие негативной обратной связи и способность извлекать из неё пользу',
    questions: [
      {
        id: 13,
        text: 'Ваш руководитель в резкой форме раскритиковал вашу работу. Как вы отреагируете?',
        options: [
          { value: 1, label: 'Обижусь и буду избегать руководителя' },
          { value: 2, label: 'Начну защищаться' },
          { value: 3, label: 'Выслушаю, но внутри буду не согласен' },
          { value: 4, label: 'Спокойно приму к сведению и задам уточняющие вопросы' },
          { value: 5, label: 'Побла годарю за обратную связь и сразу предложу конкретные шаги по улучшению' }
        ]
      },
      {
        id: 14,
        text: 'Коллеги за спиной обсуждают ваши ошибки. Вы узнали об этом. Что сделаете?',
        options: [
          { value: 1, label: 'Сделаю вид, что не знаю' },
          { value: 2, label: 'Выясню, кто это был, и выскажу претензии' },
          { value: 3, label: 'На ближайшей встрече открыто приглашу давать обратную связь лично' },
          { value: 4, label: 'Поговорю с каждым индивидуально, чтобы наладить коммуникацию' },
          { value: 5, label: 'Использую ситуацию для внедрения культуры открытой обратной связи в коллективе' }
        ]
      }
    ]
  },
  {
    title: 'Мотивация и драйв',
    description: 'Внутренняя энергия и стремление к достижениям, несмотря на препятствия',
    questions: [
      {
        id: 15,
        text: 'Вам говорят: "Эта работа не для вас, вы не справитесь". Что вы чувствуете и делаете?',
        options: [
          { value: 1, label: 'Соглашусь и отступлю' },
          { value: 2, label: 'Обижусь, но ничего не предприму' },
          { value: 3, label: 'За Motivation появится желание доказать обратное' },
          { value: 4, label: 'Скажу, что готов принять вызов и покажу результат' },
          { value: 5, label: 'Восприму как топливо для достижений и поставлю перед собой амбициозную цель' }
        ]
      },
      {
        id: 16,
        text: 'Проект, в который вы вложили много сил, закрывают. Ваши действия?',
        options: [
          { value: 1, label: 'Опущу руки' },
          { value: 2, label: 'Буду жалеть о потраченном времени' },
          { value: 3, label: 'Проанализирую причины и сделаю выводы' },
          { value: 4, label: 'Предложу, как можно использовать наработки в других проектах' },
          { value: 5, label: 'Быстро переключусь на новую задачу, сохраняя энтузиазм и позитив' }
        ]
      }
    ]
  }
];

const interpretation: Record<string, { low: string; medium: string; high: string }> = {
  Стрессоустойчивость: {
    low: 'Вы тяжело переносите стрессовые ситуации. Рекомендуется освоить техники релаксации и тайм-менеджмента.',
    medium: 'Вы способны справляться со стрессом, но иногда он влияет на продуктивность. Развивайте эмоциональную устойчивость.',
    high: 'Вы отлично держите удар и сохраняете ясность мышления в самых напряжённых обстоятельствах.'
  },
  'Управление эмоциями': {
    low: 'Эмоции часто берут верх. Практикуйте осознанность и техники эмоциональной регуляции.',
    medium: 'Вы контролируете эмоции, но иногда допускаете импульсивные реакции. Учитесь делать паузу перед ответом.',
    high: 'Вы демонстрируете высокий эмоциональный интеллект. Ваше спокойствие передаётся окружающим.'
  },
  Аргументация: {
    low: 'Вам трудно отстаивать свою точку зрения. Развивайте навыки аргументации и публичных выступлений.',
    medium: 'Вы можете аргументировать, но иногда не хватает уверенности или структуры. Тренируйтесь на дебатах.',
    high: 'Вы умеете убеждать, опираясь на логику и факты. Ваша аргументация вызывает уважение.'
  },
  Уверенность: {
    low: 'Вам не хватает веры в свои силы. Работайте над самооценкой и фиксируйте достижения.',
    medium: 'Вы уверены в себе, но сомнения могут возникать в новых ситуациях. Напоминайте себе о прошлых успехах.',
    high: 'Ваша уверенность обоснована и заразительна. Вы не боитесь трудностей и готовы брать на себя ответственность.'
  },
  'Гибкость мышления': {
    low: 'Вы предпочитаете привычные методы. Развивайте креативность и открытость новому.',
    medium: 'Вы способны адаптироваться, но иногда цепляетесь за старые схемы. Пробуйте нестандартные подходы.',
    high: 'Вы легко переключаетесь и находите инновационные решения. Гибкость — ваше конкурентное преимущество.'
  },
  'Честность / искренность': {
    low: 'Вы склонны к компромиссам с правдой. Помните, что репутация строится на доверии.',
    medium: 'Вы стараетесь быть честным, но в сложных ситуациях можете уклониться. Будьте последовательны в принципах.',
    high: 'Ваша честность вызывает уважение. Вы не боитесь говорить правду, даже когда это трудно.'
  },
  'Работа с критикой': {
    low: 'Критика вас демотивирует. Учитесь воспринимать её как инструмент роста.',
    medium: 'Вы принимаете критику, но иногда болезненно. Работайте над отделением личности от рабочих моментов.',
    high: 'Вы конструктивно воспринимаете обратную связь и используете её для непрерывного улучшения.'
  },
  'Мотивация и драйв': {
    low: 'Вам не хватает внутренней энергии. Найдите занятие, которое вас по-настоящему вдохновляет.',
    medium: 'У вас есть мотивация, но она может колебаться. Ставьте перед собой краткосрочные цели для поддержания тонуса.',
    high: 'Вы обладаете мощным внутренним драйвом. Препятствия только разжигают ваше желание добиться успеха.'
  }
};

// ------------------- компонент -------------------
const ProvocationInterview: React.FC = () => {
  const navigate = useNavigate();
  const [currentScaleIndex, setCurrentScaleIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);

  const allQuestions = useMemo(() => scales.flatMap(s => s.questions), []);

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const currentScale = scales[currentScaleIndex];
  const currentQuestions = currentScale.questions;
  const allAnswered = allQuestions.every(q => answers[q.id] !== undefined);

  const goNext = () => {
    if (currentScaleIndex < scales.length - 1) {
      setCurrentScaleIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentScaleIndex > 0) {
      setCurrentScaleIndex(prev => prev - 1);
    }
  };

  const handleSubmit = () => setSubmitted(true);

  const scoresByScale = useMemo(() => {
    return scales.map(scale => {
      const total = scale.questions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
      const max = scale.questions.length * 5;
      const percentage = Math.round((total / max) * 100);
      return { title: scale.title, percentage, total };
    });
  }, [answers]);

  const radarData = scoresByScale.map(s => ({
    scale: s.title,
    score: s.percentage,
    fullMark: 100,
  }));

  // ---------- страница результатов ----------
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.bgBlur1} />
        <div style={styles.bgBlur2} />

        <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
          <ArrowLeft size={18} />
          <span>К помощнику интервью</span>
        </button>

        <div style={styles.header}>
          <h1 style={styles.title}>Результаты провокационного интервью</h1>
          <p style={styles.subtitle}>Профиль стрессоустойчивости и поведенческих реакций</p>
        </div>

        <div style={styles.resultsGrid}>
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><BarChart2 size={18} /> Профиль личности</h3>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={radarData} outerRadius={120}>
                <PolarGrid strokeDasharray="3 3" />
                <PolarAngleAxis dataKey="scale" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                <Radar isAnimationActive={false} name="Уровень" dataKey="score" stroke="#ff4d4f" fill="#ff4d4f" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><Award size={18} /> Детализация по шкалам</h3>
            {scoresByScale.map(s => {
              const interp = interpretation[s.title];
              const level = s.percentage >= 70 ? 'high' : s.percentage >= 40 ? 'medium' : 'low';
              const color = level === 'high' ? '#2ecc71' : level === 'medium' ? '#f39c12' : '#e74c3c';
              return (
                <div key={s.title} style={styles.scaleRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 15 }}>{s.title}</strong>
                    <span style={{ color, fontWeight: 600 }}>{s.percentage}%</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${s.percentage}%`, background: color }} />
                  </div>
                  <p style={styles.interpretationText}>
                    {level === 'high' && interp.high}
                    {level === 'medium' && interp.medium}
                    {level === 'low' && interp.low}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => { setSubmitted(false); setAnswers({}); setCurrentScaleIndex(0); }}
          style={styles.resetButton}
        >
          Пройти заново
        </button>
      </div>
    );
  }

  // ---------- вопросы ----------
  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />

      <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
        <ArrowLeft size={18} />
        <span>К помощнику интервью</span>
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Метод провокация</h1>
        <p style={styles.subtitle}>
          Ответьте на провокационные вопросы, чтобы оценить свою стрессоустойчивость и поведение в сложных ситуациях.
        </p>
      </div>

      <div style={styles.progressIndicator}>
        {scales.map((_, idx) => (
          <div
            key={idx}
            style={{
              ...styles.dot,
              background: idx <= currentScaleIndex ? '#ff4d4f' : '#e0e0e0',
            }}
          />
        ))}
      </div>

      <div style={styles.card}>
        <h2 style={styles.scaleTitle}>
          <Target size={20} /> {currentScale.title}
        </h2>
        <p style={styles.scaleDesc}>{currentScale.description}</p>

        {currentQuestions.map(q => (
          <div key={q.id} style={styles.questionBlock}>
            <p style={styles.questionText}>{q.text}</p>
            <div style={styles.optionsGrid}>
              {q.options.map(opt => (
                <button
                  key={opt.value}
                  style={{
                    ...styles.optionButton,
                    background: answers[q.id] === opt.value ? 'linear-gradient(135deg, #ff4d4f, #c0392b)' : 'rgba(255,255,255,0.5)',
                    color: answers[q.id] === opt.value ? 'white' : '#1a1a2e',
                    border: answers[q.id] === opt.value ? 'none' : '1px solid rgba(0,0,0,0.1)',
                  }}
                  onClick={() => handleAnswer(q.id, opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={styles.navButtons}>
          <button onClick={goPrev} disabled={currentScaleIndex === 0} style={styles.navButton}>
            Назад
          </button>
          <span style={{ color: '#666' }}>
            {currentScaleIndex + 1} / {scales.length}
          </span>
          {currentScaleIndex < scales.length - 1 ? (
            <button onClick={goNext} style={styles.navButton}>Далее</button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              style={{
                ...styles.navButton,
                background: allAnswered ? 'linear-gradient(135deg, #2ecc71, #27ae60)' : '#ccc',
                color: 'white',
                border: 'none',
              }}
            >
              Завершить и получить результат
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------- стили -------------------
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '80px 20px 20px 20px',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 100%)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    position: 'relative',
    overflowX: 'clip',                     // вместо 'hidden', чтобы не создавать контекст скролла
    willChange: 'transform',              // ускоряет композитинг
    transform: 'translateZ(0)',           // создаёт отдельный слой
  },
  bgBlur1: {
    position: 'absolute', top: '-10%', left: '-5%', width: 600, height: 600,
    background: 'radial-gradient(circle, rgba(106,17,203,0.12) 0%, rgba(106,17,203,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute', bottom: '-10%', right: '-5%', width: 700, height: 700,
    background: 'radial-gradient(circle, rgba(37,117,252,0.12) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  backButton: {
    position: 'absolute', top: 24, left: 24,
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 40,
    padding: '10px 20px', fontSize: 15, fontWeight: 500, color: '#2575fc',
    cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  header: { textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 },
  title: {
    fontSize: 36, fontWeight: 700,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: '0 0 8px',
  },
  subtitle: { fontSize: 16, color: '#666', margin: 0 },
  progressIndicator: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24, position: 'relative', zIndex: 1 },
  dot: { width: 12, height: 12, borderRadius: '50%', transition: 'background 0.3s' },
  card: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: '40px 32px', border: '1px solid rgba(0,0,0,0.06)',
    maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1,
  },
  scaleTitle: { fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#1a1a2e' },
  scaleDesc: { fontSize: 15, color: '#4a4a68', marginBottom: 28 },
  questionBlock: { marginBottom: 28 },
  questionText: { fontSize: 16, fontWeight: 500, marginBottom: 12, color: '#1a1a2e' },
  optionsGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionButton: {
    textAlign: 'left' as const, padding: '14px 20px', borderRadius: 16, cursor: 'pointer',
    transition: 'all 0.2s', fontSize: 14, border: '1px solid rgba(0,0,0,0.1)',
  },
  navButtons: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32 },
  navButton: {
    padding: '10px 24px', borderRadius: 30, border: '1px solid #2575fc', background: 'transparent',
    color: '#2575fc', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s',
  },
  resultsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 28, maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  resultCard: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: 32, border: '1px solid rgba(0,0,0,0.06)',
  },
  chartTitle: { fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, color: '#1a1a2e' },
  scaleRow: { marginBottom: 20 },
  progressBar: { height: 8, background: '#e0e0e0', borderRadius: 4, marginTop: 6, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  interpretationText: { fontSize: 13, color: '#555', marginTop: 6, lineHeight: 1.5 },
  resetButton: {
    marginTop: 40, display: 'block', marginLeft: 'auto', marginRight: 'auto',
    padding: '12px 28px', borderRadius: 30, background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600,
  },
};

export default ProvocationInterview;